/**
 * Lukemat — the map's own data, asked new questions. A tab in the map's
 * drawer: branch heat, the hottest chains, edge polarity, who wrote where,
 * hot leaves, deep surprises, contested nodes, second parents. Every row
 * that names nodes lights them on the map (onFocusNodes), so a reading is
 * a place to go, not a number to admire.
 *
 * Reads the readings endpoint (API_ENDPOINTS.IMPACT_PATH_READINGS, mapReadingsService).
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Flame, Route, GitFork, Users, Leaf, Sparkles, Scale, GitMerge, RefreshCw } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Content = styled.div`min-width: 0; padding: 14px 14px 24px; display: flex; flex-direction: column; gap: 18px; color: #e5e7eb;`;
const Section = styled.section`display: flex; flex-direction: column; gap: 6px;`;
const H = styled.h3`margin: 0; display: flex; align-items: center; gap: 8px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #9ca3af; svg { opacity: 0.8; }`;
const Hint = styled.p`margin: 0; font-size: 0.76rem; color: #6b7280; line-height: 1.4;`;
const Totals = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
  div { background: #1a1a26; border: 1px solid #2f2f40; border-radius: 8px; padding: 8px 10px; }
  b { display: block; font-size: 1.1rem; font-weight: 700; color: #e8e8ef; font-variant-numeric: tabular-nums; }
  span { font-size: 0.68rem; color: #9ca3af; letter-spacing: 0.04em; text-transform: uppercase; }
`;
const Row = styled.button`
  display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; width: 100%; text-align: left;
  padding: 6px 8px; border-radius: 8px; border: 1px solid transparent; background: transparent; color: #e5e7eb; font: inherit; font-size: 0.82rem; line-height: 1.3; cursor: pointer;
  &:hover { background: rgba(255,255,255,0.05); border-color: #2f2f40; }
  &:focus-visible { outline: 1px solid #a78bfa; }
  .t { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sub { display: block; font-size: 0.72rem; color: #9ca3af; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .n { font-variant-numeric: tabular-nums; font-size: 0.78rem; color: #c9cbd6; white-space: nowrap; }
  .n b { color: #e8e8ef; }
`;
const Bar = styled.span`
  display: block; height: 4px; border-radius: 2px; background: #2f2f40; margin-top: 3px; overflow: hidden;
  i { display: block; height: 100%; width: ${(p) => Math.max(2, Math.min(100, p.$pct))}%; background: ${(p) => p.$color || '#a78bfa'}; }
`;
const Split = styled.span`
  display: inline-flex; height: 6px; width: 64px; border-radius: 3px; overflow: hidden; vertical-align: middle; margin-left: 6px;
  i { display: block; height: 100%; } .u { background: #3ecf8e; } .d { background: #e0708a; }
`;
const Polarity = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px;
  span { font-size: 0.72rem; padding: 2px 8px; border-radius: 999px; background: #1a1a26; border: 1px solid #2f2f40; color: #c9cbd6; font-variant-numeric: tabular-nums; }
  span.neg { border-color: rgba(224,112,138,0.5); color: #e0708a; }
`;
const Chain = styled.button`
  display: block; width: 100%; text-align: left; padding: 8px 10px; border-radius: 8px; border: 1px solid #2f2f40; background: #1a1a26; color: #e5e7eb; font: inherit; font-size: 0.78rem; line-height: 1.45; cursor: pointer;
  &:hover { border-color: #4a4a66; } &:focus-visible { outline: 1px solid #a78bfa; }
  .head { display: flex; justify-content: space-between; font-size: 0.7rem; color: #9ca3af; margin-bottom: 4px; b { color: #e8e8ef; } }
  .step { color: #c9cbd6; } .step b { color: #e8e8ef; font-weight: 600; } .arrow { color: #6b7280; }
`;
const Empty = styled.p`margin: 0; font-size: 0.78rem; color: #6b7280;`;
const Reload = styled.button`
  align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.14); background: transparent; color: #9ca3af; font: inherit; font-size: 0.74rem; cursor: pointer;
  &:hover { color: #e8e8ef; background: rgba(255,255,255,0.05); }
`;

const NEG = new Set(['weakens', 'blocks', 'nullifies', 'delays']);
const DATE_LOCALE = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-FI' };

export default function MapReadings({ instanceId, onFocusNodes, data: given = null }) {
  const { t, language } = useTranslation();
  const r = (key, vars) => t(`impactMapV2.readings.${key}`, vars);
  const [data, setData] = useState(given);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const load = async () => {
    if (!instanceId) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.IMPACT_PATH_READINGS(instanceId));
      if (!r.ok) throw new Error(`${r.status}`);
      setData(await r.json());
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  useEffect(() => { if (!given) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [instanceId, given]);

  if (error) return <Content><Empty>{r('loadFailed', { message: error })}</Empty><Reload type="button" onClick={load}><RefreshCw size={12} /> {r('retry')}</Reload></Content>;
  if (!data) return <Content><Empty>{busy ? r('reading') : r('none')}</Empty></Content>;

  const tot = data.totals || {};
  const go = (ids) => onFocusNodes?.(ids.filter(Boolean));
  const maxBranch = Math.max(1, ...(data.branches || []).map((b) => b.upPerNode));

  return (
    <Content>
      <Totals>
        <div><b>{tot.consequences}</b><span>{r('totals.consequences')}</span></div>
        <div><b>{tot.up}</b><span>{r('totals.up')}</span></div>
        <div><b>{tot.voted}</b><span>{r('totals.voted')}</span></div>
        <div><b>{tot.upPerNode}</b><span>{r('totals.upPerNode')}</span></div>
        <div><b>{tot.leafShare} %</b><span>{r('totals.leaves')}</span></div>
        <div><b>{tot.grounded}</b><span>{r('totals.grounded')}</span></div>
      </Totals>

      <Section>
        <H><Flame size={14} /> {r('heat.title')}</H>
        <Hint>{r('heat.hint', { mean: tot.upPerNode })}</Hint>
        {(data.branches || []).slice(0, 10).map((b) => (
          <Row key={b.id} type="button" onClick={() => go([b.id])} title={r('heat.rowTitle', { nodes: b.nodes, writers: b.writers, grounded: b.grounded, depth: b.maxDepth })}>
            <span className="t">{b.title}<span className="sub">{r('heat.rowSub', { scenario: b.scenarioName, nodes: b.nodes, writers: b.writers })}</span><Bar $pct={(100 * b.upPerNode) / maxBranch} $color="#f0b35e"><i /></Bar></span>
            <span className="n"><b>{b.upPerNode}</b> {r('heat.perNode')}</span>
          </Row>
        ))}
      </Section>

      <Section>
        <H><Route size={14} /> {r('chains.title')}</H>
        <Hint>{r('chains.hint')}</Hint>
        {(data.chains || []).slice(0, 3).map((c, i) => (
          <Chain key={i} type="button" onClick={() => go(c.nodes.map((n) => n.id))}>
            <div className="head"><span>{r('chains.steps', { scenario: c.scenarioName, depth: c.depth })}</span><span><b>+{c.up}</b></span></div>
            {c.nodes.map((n, j) => (
              <span key={n.id} className="step">{j > 0 && <span className="arrow"> → </span>}{n.up >= 10 ? <b>{n.text}</b> : n.text}</span>
            ))}
          </Chain>
        ))}
        {!(data.chains || []).length && <Empty>{r('chains.none')}</Empty>}
      </Section>

      <Section>
        <H><GitFork size={14} /> {r('polarity.title')}</H>
        <Hint>{r('polarity.hint', { share: data.polarity?.negativeShare })}</Hint>
        <Polarity>
          {Object.entries(data.polarity?.counts || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <span key={k} className={NEG.has(k) ? 'neg' : ''}>{t(`impactMapV2.effectTypes.${k}`)} {v}</span>
          ))}
        </Polarity>
      </Section>

      <Section>
        <H><Users size={14} /> {r('authorship.title')}</H>
        <Hint>{r('authorship.hint', { mean: data.authorship?.meanWritersPerBranch, writers: tot.writers })}</Hint>
        {(data.authorship?.monologues || []).length ? (
          <>
            <Hint>{r('authorship.monologues')}</Hint>
            {data.authorship.monologues.map((m) => (
              <Row key={m.id} type="button" onClick={() => go([m.id])}><span className="t">{m.title}</span><span className="n">{r('authorship.nodes', { count: m.nodes })}</span></Row>
            ))}
          </>
        ) : <Empty>{r('authorship.none')}</Empty>}
      </Section>

      <Section>
        <H><Sparkles size={14} /> {r('surprises.title')}</H>
        <Hint>{r('surprises.hint')}</Hint>
        {(data.surprises || []).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}>
            <span className="t">{n.text}<span className="sub">{r('surprises.sub', { depth: n.depth, parent: n.parentText, up: n.parentUp })}</span></span>
            <span className="n"><b>+{n.up}</b></span>
          </Row>
        ))}
        {!(data.surprises || []).length && <Empty>{r('surprises.none')}</Empty>}
      </Section>

      <Section>
        <H><Scale size={14} /> {r('contested.title')}</H>
        <Hint>{r('contested.hint')}</Hint>
        {(data.contested || []).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}>
            <span className="t">{n.text}<span className="sub">{n.scenarioName}</span></span>
            <span className="n">+{n.up} −{n.down}<Split><i className="u" style={{ width: `${(100 * n.up) / (n.up + n.down)}%` }} /><i className="d" style={{ width: `${(100 * n.down) / (n.up + n.down)}%` }} /></Split></span>
          </Row>
        ))}
        {!(data.contested || []).length && <Empty>{r('contested.none')}</Empty>}
      </Section>

      <Section>
        <H><Leaf size={14} /> {r('hotLeaves.title')}</H>
        <Hint>{r('hotLeaves.hint')}</Hint>
        {(data.hotLeaves || []).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}>
            <span className="t">{n.text}<span className="sub">{r('hotLeaves.sub', { scenario: n.scenarioName, depth: n.depth })}</span></span>
            <span className="n"><b>+{n.up}</b></span>
          </Row>
        ))}
      </Section>

      <Section>
        <H><GitMerge size={14} /> {r('multiParent.title')}</H>
        <Hint>{r('multiParent.hint')}</Hint>
        {(data.multiParent || []).slice(0, 8).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}><span className="t">{n.text}</span><span className="n">{r('multiParent.parents', { count: n.parents })}</span></Row>
        ))}
        {!(data.multiParent || []).length && <Empty>{r('multiParent.none')}</Empty>}
      </Section>

      {data.writing && (
        <Hint>{r('written', {
          from: new Date(data.writing.first).toLocaleString(DATE_LOCALE[language] || 'fi-FI', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }),
          to: new Date(data.writing.last).toLocaleTimeString(DATE_LOCALE[language] || 'fi-FI', { hour: '2-digit', minute: '2-digit' }),
          nodes: data.writing.nodes,
        })}</Hint>
      )}
      <Reload type="button" onClick={load} disabled={busy}><RefreshCw size={12} /> {busy ? r('rereading') : r('reread')}</Reload>
    </Content>
  );
}
